import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authLogin, authMe, setAuthToken } from "@/lib/dentiumApi";
import { useClinic } from "@/hooks/useClinic";

export const Login = ({ onAuthed }) => {
  const [email, setEmail] = useState("admin@dentium.com");
  const [password, setPassword] = useState("Dentium2026!");
  const [loading, setLoading] = useState(false);
  const clinic = useClinic();
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await authLogin(email.trim(), password);
      setAuthToken(data.access_token);
      const me = await authMe();
      onAuthed(me);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "No se pudo iniciar sesión. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-[420px]"
        data-testid="login-page"
      >
        <div className="flex justify-center mb-8">
          <img src="/dentium-logo.png" alt={clinic.name || "Logotipo"} className="h-12 w-auto" />
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-2xl">
          <h1 className="font-display text-2xl font-normal text-[#1A1A1A] text-center">Panel interno</h1>
          <p className="text-sm text-[#8A7E70] text-center mt-1.5 mb-7"><span data-testid="login-clinic-name">Acceso exclusivo para el equipo de {clinic.name || "la clínica"}</span></p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#6B6157] mb-1.5 block">Correo electrónico</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A79E90]" />
                <Input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tucorreo@tuclinica.com"
                  className="pl-9 h-11 border-[#E7E2DA]"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[#6B6157] mb-1.5 block">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A79E90]" />
                <Input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 h-11 border-[#E7E2DA]"
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#1A1A1A] hover:bg-black text-white disabled:opacity-70"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Accediendo…</> : "Iniciar sesión"}
            </Button>
          </form>

          <div className="flex items-center justify-center gap-1.5 mt-6 text-[11px] text-[#A79E90]">
            <ShieldCheck className="h-3.5 w-3.5" />
            Herramienta interna · datos cifrados (RGPD)
          </div>
        </div>
      </motion.div>
    </div>
  );
};
